import { Component, Injector, Input, ViewContainerRef } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { Observable, filter, tap } from 'rxjs';

import { AngleProcessingAvailability, OutlineEditorOptions } from 'cloud-shared-lib';
import { AngleMode, AnglePosition, OutlineAngle } from '../../../models/cutting/cutting-panel-outline.model';
import { Position } from '../../../models/cutting/cutting-svg.model';
import { FacadeView } from '../../../models/cutting/facade-view.model';
import { AngleProcessingNamePipe } from '../../../pipes/angle-processing-name.pipe';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import {
  AngleProcessingDialogComponent,
  AngleProcessingDialogData,
} from '../angle-processing-dialog/angle-processing-dialog.component';
import { FacadeSettingsStateService } from '../facade-settings-state.service';
import { FacadesAvailableComponentsService } from '../facades-available-components.service';

type Angles = {
  topRight: OutlineAngle;
  bottomRight: OutlineAngle;
  bottomLeft: OutlineAngle;
  topLeft: OutlineAngle;
};

export class AnglesProcessingBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _AnglesProcessingMixinBase = mixinDisabled(AnglesProcessingBase);

@Component({
  selector: 'csl-angles-processing',
  templateUrl: './angles-processing.component.html',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class AnglesProcessingComponent extends _AnglesProcessingMixinBase implements CanDisable {
  @Input() angleProcessingAvailability: AngleProcessingAvailability;
  @Input() outlineEditorOptions: OutlineEditorOptions;
  action$: Observable<any>;
  data$: Observable<any>;

  panel: FacadeView;

  anglesWithHandle: Set<Position> = new Set();
  topLeftNotAvailableToAdd = false;
  topRightNotAvailableToAdd = false;
  bottomRightNotAvailableToAdd = false;
  bottomLeftNotAvailableToAdd = false;

  constructor(
    private facadeSettingsService: FacadeSettingsStateService,
    private dialogs: LegacyDialogsService,
    private viewContainerRef: ViewContainerRef,
    private injector: Injector
  ) {
    super();

    this.data$ = this.facadeSettingsService.panel$.pipe(
      tap((p: FacadeView) => (this.panel = p)),
      tap(p => {
        this.checkAnglesAvailability(p);
      })
    );
  }

  checkAnglesAvailability(panel: FacadeView): void {
    this.anglesWithHandle = this.getAnglesWithHandle(panel);
    this.topLeftNotAvailableToAdd = this.anglesWithHandle.has('tl');
    this.topRightNotAvailableToAdd = this.anglesWithHandle.has('tr');
    this.bottomRightNotAvailableToAdd = this.anglesWithHandle.has('br');
    this.bottomLeftNotAvailableToAdd = this.anglesWithHandle.has('bl');
  }

  getAnglesWithHandle(panel: FacadeView): Set<Position> {
    const angles = new Set<Position>();

    if (!panel) {
      return angles;
    }

    if (panel.panelOutline.topSide.handle) {
      angles.add('tl');
      angles.add('tr');
    }

    if (panel.panelOutline.rightSide.handle) {
      angles.add('tr');
      angles.add('br');
    }

    if (panel.panelOutline.bottomSide.handle) {
      angles.add('br');
      angles.add('bl');
    }

    if (panel.panelOutline.leftSide.handle) {
      angles.add('bl');
      angles.add('tl');
    }

    return angles;
  }

  selectProcessing(angle: AnglePosition): void {
    const parentInjector = this.injector;

    this.action$ = this.dialogs
      .component<AngleProcessingDialogData, Angles>(
        AngleProcessingDialogComponent,
        {
          angle: angle,
          angleProcessingAvailability: this.angleProcessingAvailability,
          outlineEditorOptions: this.outlineEditorOptions,
        },
        800,
        {
          viewContainerRef: this.viewContainerRef,
          injector: Injector.create({
            providers: [
              {
                provide: FacadesAvailableComponentsService,
                useValue: parentInjector.get(FacadesAvailableComponentsService),
              },
            ],
            parent: parentInjector,
          }),
        }
      )
      .pipe(
        filter(r => !!r),
        tap(e => {
          this.panel.panelOutline.topRight = e.topRight;
          this.panel.panelOutline.bottomRight = e.bottomRight;
          this.panel.panelOutline.bottomLeft = e.bottomLeft;
          this.panel.panelOutline.topLeft = e.topLeft;

          this.facadeSettingsService.updatePanel(this.panel);
        })
      );
  }

  getDescription(mode: AngleMode, angle: Position): string {
    if (!mode && this.anglesWithHandle.has(angle)) {
      return $localize`Невозможно добавить из-за ручки`;
    }

    if (!mode) {
      return $localize`Выберите обработку`;
    }

    return new AngleProcessingNamePipe().transform(mode);
  }

  getIconLink(mode: AngleMode): string {
    if (!mode) {
      return '';
    }

    const iconPath = (name: string) => `./assets/images/angle-processings/${name}.svg`;

    switch (mode) {
      case AngleMode.Chamfer:
        return iconPath('chamfer');

      case AngleMode.Cutted:
        return iconPath('cut');

      case AngleMode.CuttedInside:
        return iconPath('cut-inside');

      case AngleMode.CuttedInsideWithAngle:
        return iconPath('cut-inside-with-angle');

      case AngleMode.DiagonalCut:
        return iconPath('diagonal-cut');

      case AngleMode.Rounded:
        return iconPath('round');

      case AngleMode.RoundedInside:
        return iconPath('round-inside');

      default:
        return '';
    }
  }
}
